import { ThunkAction } from 'redux-thunk';
import * as Actions from './actions';
import { AnyAction } from 'redux';
import { AppState } from '../../store';
import moment from 'moment';
import { v2BaseApiPath } from '../../../config/baseConfig';
import { SumamoruAdditionalServices, guideAppType } from '../property/types';
import { isContractorOnly, isOccupantOnly } from '../invitation/operations';
import { ApplicantType, getApplicantText } from '../invitation/types';
import { submitErrorStatus } from './types';

export interface ActivationContext {
  organizationId: string;
  usageInfoId: string;
  applicantType: ApplicantType;
}

export const completeApplication = async (
  activation: ActivationContext,
  linkage: string | undefined,
  headers: { [key: string]: string }
): Promise<submitErrorStatus> => {
  const { organizationId, usageInfoId, applicantType } = activation;
  const isContractorUser = isContractorOnly(applicantType);

  // 契約者のみは linkageId を使わない。
  // 欠けたまま呼んでもサーバは 400 を返すので、呼ばずに失敗として扱う
  if (
    !applicantType ||
    !organizationId ||
    !usageInfoId ||
    (!isContractorUser && !linkage)
  ) {
    console.error(
      'failed to complete application. required value is missing.',
      { applicantType, organizationId, usageInfoId, linkage }
    );
    return 'internal';
  }

  const contractorActivateUrl = `${v2BaseApiPath}/application/complete/contractor`;
  const activateUrl = `${v2BaseApiPath}/application/complete`;

  const endpoint = isContractorUser ? contractorActivateUrl : activateUrl;
  const method = 'POST';

  // 完了対象の申込みを特定する項目。両APIに共通で必要
  const applicationTarget = { organizationId, usageInfoId };
  // 入居者・契約者兼入居者は、これに申込者種別と連携番号を足す
  const body = JSON.stringify(
    isContractorUser
      ? applicationTarget
      : { ...applicationTarget, applicantType, linkageId: linkage }
  );

  try {
    const res = await fetch(endpoint, {
      method,
      headers,
      body,
      mode: 'cors'
    });

    if (res.status !== 200) {
      if (res.status === 409) {
        return 'conflict';
      }
      return 'internal';
    }
    return 'no_error';
  } catch (error) {
    console.error('failed to complete application.', error);
    return 'internal';
  }
};

export const submitParamater = (
  activation: ActivationContext
): ThunkAction<Promise<void>, AppState, {}, AnyAction> => async (
  dispatch,
  getState
) => {
  try {
    dispatch(Actions.submitParamater());

    const formParam = getState().applyForm.parameters;
    const email = getState().user.email;
    const access_token = getState().user.access_token;
    const refresh_token = getState().user.refresh_token;
    const user_id = getState().user.user_id;
    const propParam = getState().property.property;
    const phoneNumber = getState().user.phoneNumber;
    const method = 'POST';

    const isContractorUser = isContractorOnly(activation.applicantType);
    const isOccupantUser = isOccupantOnly(activation.applicantType);

    // 契約者のみはBKPアカウント連携を行わないため access_token を持たない。
    // 認証ヘッダを付けられないので アカウント登録もスキップする
    const headers: { [key: string]: string } = isContractorUser
      ? { Accept: 'application/json', 'Content-Type': 'application/json' }
      : {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-api-key': access_token
        };
    let body = '';

    if (!isContractorUser) {
      const homeHubUserEndpoint = `${v2BaseApiPath}/accounts`;
      body = JSON.stringify({
        bkpUserId: user_id,
        name: `${formParam.family_name} ${formParam.first_name}`,
        ...(email ? { email } : {}),
        ...(phoneNumber ? { phoneNumber } : {})
      });
      const accountRes = await fetch(homeHubUserEndpoint, {
        method,
        headers,
        body,
        mode: 'cors'
      });

      // 200 / 201 / 409（登録済み）は成功として扱い、申込み作成へ進む
      const accountSucceeded =
        accountRes.status === 200 ||
        accountRes.status === 201 ||
        accountRes.status === 409;

      if (!accountSucceeded) {
        dispatch(
          Actions.failedToSubmitParamater(
            accountRes.status === 401 ? 'unauthorized' : 'internal'
          )
        );
        return;
      }
    }

    const endpoint = `${v2BaseApiPath}/application/create`;

    const { isSameEmail } = getState().applyForm.parameters;
    const { isCoodinatedGasStartDate } = getState().applyForm.parameters;

    let formEmail = formParam.email;

    if (isSameEmail) {
      formEmail = email;
    }

    // 申込者種別ごとに、共通項目へ contractor / occupant を足す
    // 兼任: 両方 / 入居者: occupant のみ / 契約者: contractor のみ
    const applicationTarget = {
      ...(isContractorUser ? {} : { refresh_token: refresh_token }),
      applicant_type: getApplicantText(activation.applicantType),
      organization_id: activation.organizationId,
      property_id: propParam.allSpaceId,
      contract_id: propParam.contractId,
      property_no: propParam.sumamoruCode,
      building_name: propParam.buildingName,
      postal_code: propParam.postCode,
      address1:
        propParam.address ||
        propParam.prefecture + propParam.city + propParam.afterCity,
      address2: propParam.buildingName + propParam.name,
      move_plan_date: moment(propParam.occupyScheduledDate).format(
        'YYYY-MM-DD'
      ),
      has_electric_contract: propParam.hasElectricContract,
      sumamoru_project_type: propParam.sumamoruProjectType,
      install_app_name: guideAppType(propParam.sumamoruInstallAppTypes),
      sumamoruAdditionalServices:
        propParam.sumamoruAdditionalServices &&
        propParam.sumamoruAdditionalServices.filter(
          (service): service is SumamoruAdditionalServices =>
            Object.values(SumamoruAdditionalServices).includes(
              service as SumamoruAdditionalServices
            )
        )
    };

    const applicantProfile = {
      family_name: formParam.family_name,
      first_name: formParam.first_name,
      family_name_kana: formParam.family_name_kana,
      first_name_kana: formParam.first_name_kana,
      email: formEmail,
      phone_number: formParam.phone_number,
      phone_number_after_move: formParam.phone_number,
      ...(isContractorUser
        ? {}
        : {
            birthday: formParam.birthday,
            sex: parseInt(formParam.gender, 10)
          })
    };

    const contractor = {
      ...applicantProfile,
      phone_number_when_move: formParam.phone_number_when_move,
      pay_method: parseInt(formParam.pay_method, 10),
      use_og_gas: parseInt(formParam.use_og_gas, 10),
      start_power_date: getDateString(formParam.start_power_date),
      start_gas_date: isCoodinatedGasStartDate
        ? undefined
        : getDateString(formParam.start_gas_date),
      start_gas_time: isCoodinatedGasStartDate
        ? undefined
        : formParam.start_gas_time
    };

    const occupant = {
      ...applicantProfile,
      bkp_user_id: user_id,
      usage_info_id: propParam.usageInfoId,
      persona_id: propParam.peopleId
    };

    body = JSON.stringify({
      ...applicationTarget,
      ...(isOccupantUser ? {} : { contractor }),
      ...(isContractorUser ? {} : { occupant })
    });

    await fetch(endpoint, {
      method,
      headers,
      body,
      mode: 'cors'
    })
      .then(async res => {
        if (res.status === 200) {
          const json = await res.json().catch(() => ({}));
          const linkageId = json.linkage_id;
          const completeStatus = await completeApplication(
            activation,
            linkageId,
            headers
          );
          if (completeStatus === 'no_error') {
            dispatch(Actions.successToSubmitParamater());
          } else {
            dispatch(Actions.failedToSubmitParamater(completeStatus));
          }
        } else if (res.status === 401) {
          dispatch(Actions.failedToSubmitParamater('unauthorized'));
        } else if (res.status === 409) {
          dispatch(Actions.failedToSubmitParamater('conflict'));
        } else {
          dispatch(Actions.failedToSubmitParamater('internal'));
        }
      })
      .catch(error => {
        console.error(error);
        dispatch(Actions.failedToSubmitParamater('internal'));
      });
  } catch (error) {
    console.error(error);
    dispatch(Actions.failedToSubmitParamater('internal'));
  }
};

const getDateString = (date?: string): string | undefined => {
  if (!date || date.length === 0) {
    return undefined;
  }
  return moment(new Date(date)).format('YYYY-MM-DD');
};
